import { useEffect } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { Clock, BedDouble, BadgeCheck, IndianRupee, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Navbar } from "../components/medical/Navbar";
import { Footer } from "../components/medical/Footer";
import { WhatsappFloat } from "../components/medical/WhatsappFloat";
import { Breadcrumbs } from "../components/medical/Breadcrumbs";
import { SEO } from "../components/medical/SEO";
import { ConsultationForm } from "../components/medical/ConsultationForm";
import { getTreatment, getTreatmentsBySpecialty } from "../data/treatments";
import { getSpecialty } from "../data/specialties";
import { getDoctorsByTreatment } from "../data/doctors";

export const TreatmentPage = () => {
    const { slug } = useParams();
    const treatment = getTreatment(slug);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "instant" });
    }, [slug]);

    if (!treatment) return <Navigate to="/" replace />;

    const specialty = getSpecialty(treatment.specialty);
    const doctors = getDoctorsByTreatment(slug);
    const related = getTreatmentsBySpecialty(treatment.specialty)
        .filter((t) => t.slug !== slug)
        .slice(0, 4);

    const schema = {
        "@context": "https://schema.org",
        "@type": "MedicalProcedure",
        name: treatment.name,
        description: treatment.overview,
        bodyLocation: specialty?.shortName,
    };

    const facts = [
        { icon: Clock, label: "Duration", value: treatment.duration },
        { icon: BedDouble, label: "Hospital stay", value: treatment.hospitalStay },
        { icon: BadgeCheck, label: "Success rate", value: treatment.successRate },
        { icon: IndianRupee, label: "Cost", value: treatment.cost },
    ];

    return (
        <div className="min-h-screen bg-background font-body">
            <SEO
                title={treatment.name}
                description={treatment.overview.slice(0, 160)}
                image={treatment.image}
                schema={schema}
            />
            <Navbar />
            <main className="pt-32 md:pt-40">
                {/* Hero */}
                <section className="relative py-12 md:py-20 bg-gradient-to-br from-brand-50/60 via-white to-orange-50/40 border-b border-stone-200">
                    <div className="mx-auto max-w-7xl px-5 sm:px-8">
                        <Breadcrumbs
                            items={[
                                { label: "Specialities", to: "/#specialities" },
                                {
                                    label: specialty?.shortName || "Specialty",
                                    to: specialty
                                        ? `/specialties/${specialty.slug}`
                                        : "/",
                                },
                                { label: treatment.shortName },
                            ]}
                        />
                        <div className="mt-8 grid lg:grid-cols-12 gap-10 items-center">
                            <div className="lg:col-span-7">
                                <div className="text-xs uppercase tracking-[0.2em] text-orange-600 font-semibold mb-3">
                                    {specialty?.shortName}
                                </div>
                                <h1
                                    data-testid="treatment-name"
                                    className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-stone-900 leading-[1.05]"
                                >
                                    {treatment.name}
                                </h1>
                                <p className="mt-4 text-lg text-brand-700 font-semibold max-w-xl">
                                    {treatment.tagline}
                                </p>
                                <p className="mt-5 text-stone-600 leading-relaxed max-w-2xl">
                                    {treatment.overview}
                                </p>
                                <div className="mt-8 flex flex-wrap gap-3">
                                    <Button
                                        asChild
                                        data-testid="treatment-cta"
                                        className="rounded-full bg-orange-500 hover:bg-orange-600 text-white px-7 h-12"
                                    >
                                        <a href="#book">Get Free Quote</a>
                                    </Button>
                                </div>
                            </div>
                            <div className="lg:col-span-5">
                                <div className="aspect-[5/4] rounded-3xl overflow-hidden shadow-2xl ring-1 ring-black/5">
                                    <img
                                        src={treatment.image}
                                        alt={treatment.name}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Quick facts */}
                        <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4">
                            {facts.map((f) => (
                                <div
                                    key={f.label}
                                    className="p-5 rounded-2xl bg-white border border-stone-200"
                                >
                                    <f.icon className="h-5 w-5 text-orange-500" />
                                    <div className="mt-3 text-xs uppercase tracking-wider text-stone-500">
                                        {f.label}
                                    </div>
                                    <div className="mt-1 font-display text-lg font-bold text-stone-900">
                                        {f.value}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Procedure + Recovery */}
                <section className="py-16 md:py-20">
                    <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-10">
                        <div className="lg:col-span-8 space-y-10">
                            <div>
                                <h2 className="font-display text-3xl font-bold text-stone-900">
                                    The Procedure
                                </h2>
                                <p className="mt-4 text-stone-600 leading-relaxed">
                                    {treatment.procedure}
                                </p>
                            </div>
                            <div>
                                <h2 className="font-display text-3xl font-bold text-stone-900">
                                    Recovery & Aftercare
                                </h2>
                                <p className="mt-4 text-stone-600 leading-relaxed">
                                    {treatment.recovery}
                                </p>
                            </div>
                        </div>
                        <div className="lg:col-span-4">
                            <div id="book" className="lg:sticky lg:top-32">
                                <ConsultationForm
                                    context={`Treatment: ${treatment.name}`}
                                    treatmentSuggestion={treatment.shortName}
                                    title="Get a Free Quote"
                                    subtitle="Share your reports — get doctor's opinion + cost estimate within 24 hours."
                                />
                            </div>
                        </div>
                    </div>
                </section>

                {/* Doctors performing this */}
                {doctors.length > 0 && (
                    <section className="py-16 md:py-20 bg-stone-50/60 border-y border-stone-200">
                        <div className="mx-auto max-w-7xl px-5 sm:px-8">
                            <h2 className="font-display text-3xl sm:text-4xl font-bold text-stone-900 max-w-xl">
                                Doctors performing this treatment
                            </h2>
                            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                                {doctors.map((d) => (
                                    <Link
                                        key={d.slug}
                                        to={`/doctors/${d.slug}`}
                                        data-testid={`treatment-doctor-${d.slug}`}
                                        className="group rounded-3xl bg-white border border-stone-200 overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all"
                                    >
                                        <div className="aspect-[4/5] overflow-hidden bg-stone-100">
                                            <img
                                                src={d.image}
                                                alt={d.name}
                                                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                                            />
                                        </div>
                                        <div className="p-5">
                                            <div className="text-[11px] uppercase tracking-wider text-brand-700 font-semibold">
                                                {d.specialtyLabel}
                                            </div>
                                            <div className="font-display text-lg font-bold text-stone-900 mt-1">
                                                {d.name}
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </section>
                )}

                {/* Related Treatments */}
                {related.length > 0 && (
                    <section className="py-16 md:py-20">
                        <div className="mx-auto max-w-7xl px-5 sm:px-8">
                            <h2 className="font-display text-3xl sm:text-4xl font-bold text-stone-900">
                                Related Treatments
                            </h2>
                            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                                {related.map((t) => (
                                    <Link
                                        key={t.slug}
                                        to={`/treatments/${t.slug}`}
                                        data-testid={`related-treatment-${t.slug}`}
                                        className="group p-5 rounded-2xl bg-white border border-stone-200 hover:border-brand-400 hover:shadow-md transition-all flex items-center justify-between"
                                    >
                                        <div>
                                            <div className="font-display font-semibold text-stone-900">
                                                {t.shortName}
                                            </div>
                                            <div className="text-xs text-stone-500 mt-1">
                                                {t.tagline}
                                            </div>
                                        </div>
                                        <ArrowRight className="h-4 w-4 text-stone-400 group-hover:text-brand-700" />
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </section>
                )}
            </main>
            <Footer />
            <WhatsappFloat />
        </div>
    );
};
